import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Download, Image, Palette, Type, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { V5Shell, CARD_SHADOW } from "@/components/ios";
import { LoadingMark } from "@/components/Logo";

const LOGO_DOWNLOADS = [
  { label: "Master logo (1331px PNG)", href: "/doclyx-icon.png", testid: "brand-dl-master" },
  { label: "Web logo (512px PNG)", href: "/doclyx-icon-512.png", testid: "brand-dl-512" },
  { label: "Web logo (256px PNG)", href: "/doclyx-icon-256.png", testid: "brand-dl-256" },
  { label: "Favicon (ICO)", href: "/favicon.ico", testid: "brand-dl-favicon" },
  { label: "Apple touch icon", href: "/apple-touch-icon.png", testid: "brand-dl-apple" },
];

const COLORS = [
  { name: "DX Navy", hex: "#002B5B", note: "The accent — links, active states, focus rings" },
  { name: "Ink", hex: "#111110", note: "Headlines, body text, primary buttons" },
  { name: "Paper", hex: "#FDFDFB", note: "Page background" },
  { name: "Slate", hex: "#52524F", note: "Secondary text" },
  { name: "Mist", hex: "#6E6E68", note: "Captions, micro-labels" },
  { name: "DX Sky", hex: "#6FA3E8", note: "Accent on the dark workspace theme" },
];

const TYPE_RULES = [
  { name: "Instrument Sans", role: "Interface & body", sample: "Precision document infrastructure" },
  { name: "Playfair Display", role: "Editorial headlines", sample: "Close the deal" },
  { name: "JetBrains Mono", role: "Labels & data", sample: "16 ENGINES · AES-256" },
];

const USAGE_NOTES = [
  "Always place the DX icon on a white or very light tile — never directly on dark photography.",
  "Keep clearspace around the mark equal to the height of the icon's inner D stem.",
  "Do not recolor, rotate, or add effects to the mark; use the files below as-is.",
  "Pair the icon with the 'Doclyx' wordmark in Instrument Sans medium, tracking tight.",
];

const Section = ({ icon: Icon, title, children, className = "", testid }) => (
  <section className={`bg-white rounded-3xl p-6 md:p-8 ${CARD_SHADOW} ${className}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-5">
      <Icon className="w-4 h-4 text-[#007AFF]" strokeWidth={1.5} />
      <h2 className="text-[17px] font-semibold tracking-tight">{title}</h2>
    </div>
    {children}
  </section>
);

export default function AdminBrand() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "admin") navigate("/account");
  }, [user, navigate]);

  if (!user || user.role !== "admin") {
    return (
      <main className="bg-[#F2F2F7] min-h-screen flex items-center justify-center">
        <LoadingMark label="Loading brand kit" />
      </main>
    );
  }

  const copyHex = (hex) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(hex).then(() => toast.success(`${hex} copied`)).catch(() => {});
    }
  };

  return (
    <V5Shell title="Brand kit" subtitle="Command center" testid="brand-page">
      <Toaster position="top-center" richColors />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section icon={Image} title="Logo" testid="brand-logo-tile">
          <div className="rounded-2xl bg-[#F2F2F7] p-10 flex items-center justify-center mb-5">
            <img src="/doclyx-icon-256.png" alt="Doclyx DX logo" className="w-28 h-28" data-testid="brand-logo-preview" />
          </div>
          <div className="divide-y divide-black/[0.05]">
            {LOGO_DOWNLOADS.map((d) => (
              <a
                key={d.href}
                href={d.href}
                download
                data-testid={d.testid}
                className="flex items-center justify-between py-3 min-h-[44px] text-[15px] font-medium text-[#007AFF] active:scale-[0.99] transition-transform duration-150"
              >
                {d.label}
                <Download className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </Section>

        <Section icon={Image} title="Social card" testid="brand-social-tile">
          <div className="rounded-2xl overflow-hidden border border-black/[0.05] mb-5">
            <img src="/og/home.jpg" alt="Doclyx social share card" className="w-full" data-testid="brand-social-preview" />
          </div>
          <a
            href="/og/home.jpg"
            download
            data-testid="brand-dl-social"
            className="flex items-center justify-between py-3 min-h-[44px] text-[15px] font-medium text-[#007AFF]"
          >
            Open Graph card (1200×630)
            <Download className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <p className="text-[13px] leading-relaxed text-[#3C3C43]/55 mt-2">
            Shown automatically when doclyx.com links are shared on X, LinkedIn, iMessage, and Slack.
          </p>
        </Section>

        <Section icon={Palette} title="Colors" testid="brand-colors-tile">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {COLORS.map((c) => (
              <button
                key={c.hex}
                type="button"
                data-testid={`brand-color-${c.hex.replace("#", "")}`}
                onClick={() => copyHex(c.hex)}
                className="rounded-2xl border border-black/[0.05] overflow-hidden text-left active:scale-95 transition-transform duration-150"
                title="Tap to copy hex"
              >
                <div className="h-16" style={{ backgroundColor: c.hex }} />
                <div className="p-3">
                  <p className="text-[13px] font-semibold">{c.name}</p>
                  <p className="text-[12px] font-mono text-[#3C3C43]/60">{c.hex}</p>
                  <p className="text-[11px] text-[#3C3C43]/45 mt-0.5 min-h-[2em]">{c.note}</p>
                </div>
              </button>
            ))}
          </div>
        </Section>

        <div className="space-y-6">
          <Section icon={Type} title="Typography" testid="brand-type-tile">
            <div className="space-y-4">
              {TYPE_RULES.map((t) => (
                <div key={t.name} className="rounded-2xl bg-[#F2F2F7] p-4">
                  <p className="text-[13px] font-semibold text-[#3C3C43]/60 mb-1">
                    {t.name} · {t.role}
                  </p>
                  <p className="text-[19px] tracking-tight" style={{ fontFamily: t.name === "JetBrains Mono" ? '"JetBrains Mono", monospace' : t.name === "Playfair Display" ? '"Playfair Display", serif' : '"Instrument Sans", sans-serif' }}>
                    {t.sample}
                  </p>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={BookOpen} title="Usage notes" testid="brand-usage-tile">
            <ul className="space-y-3">
              {USAGE_NOTES.map((n, i) => (
                <li key={i} className="flex gap-3 text-[14px] leading-relaxed text-[#3C3C43]/80" data-testid={`brand-note-${i}`}>
                  <span className="w-5 h-5 rounded-full bg-[#007AFF]/10 text-[#007AFF] text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  {n}
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </V5Shell>
  );
}
